<script lang="ts">
  import type { ButtonVariant } from '$types/buttonVariant';
  import BrandedSpinner from '$components/branded-spinner/BrandedSpinner.svelte';
  import { getSvgPath } from '$utils/smoothCorners';
  import { onDestroy, onMount } from 'svelte';
  import { afterViewTransition } from '$utils/afterViewTransition';
  import { beforeNavigate } from '$app/navigation';
  import type { ButtonSize } from '$types/buttonSize';

  type Layout = 'text' | 'icon';

  interface Props {
    variant?: ButtonVariant;
    type?: 'button' | 'submit';
    icon?: boolean;
    disabled?: boolean;
    fullOpacityIcon?: boolean;
    pending?: boolean;
    size?: ButtonSize;
    ariaLabel: string;
    testid?: string;
    stretched?: boolean;
    vertical?: boolean;
    round?: boolean;
    padding?: string | undefined;
    justifyContent?: 'center' | 'between' | 'start' | 'end';
    leading?: import('svelte').Snippet;
    children?: import('svelte').Snippet;
    trailing?: import('svelte').Snippet;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onclick?: (...args: any[]) => void;
  }

  let {
    variant = 'primary',
    type = 'button',
    icon = false,
    disabled = false,
    fullOpacityIcon = false,
    pending = false,
    size = 'lg',
    ariaLabel,
    testid = 'button',
    stretched = false,
    vertical = false,
    round = false,
    padding = undefined,
    justifyContent = 'center',
    leading,
    children,
    trailing,
    onclick,
  }: Props = $props();

  // Color definitions for different variants
  const COLORS: Record<
    ButtonVariant,
    {
      fill: string;
      stroke?: string;
      strokeWidth?: number;
      fallbackBg?: string;
      fallbackBorder?: string;
    }
  > = {
    circles: {
      fill: 'fill-background-circles',
      fallbackBg: 'bg-background-circles',
    },
    primary: {
      fill: 'fill-background-brand',
      fallbackBg: 'bg-background-brand',
    },
    secondary: {
      fill: 'fill-background',
      stroke: localStorage.getItem('darkTheme') === 'true' ? '#666666' : '#EBEFF2',
      strokeWidth: 2,
      fallbackBg: 'bg-background',
      fallbackBorder: 'border-[#EBEFF2] border-2',
    },
    success: {
      fill: 'fill-background-success-muted',
      stroke: localStorage.getItem('darkTheme') === 'true' ? '#126427' : '#22C54B',
      strokeWidth: 2,
      fallbackBg: 'bg-background-success-muted',
      fallbackBorder: 'border-[#22C54B] border-2',
    },
    tertiary: {
      fill: 'fill-background-subtle',
      fallbackBg: 'bg-background-subtle',
    },
    danger: {
      fill: 'fill-background-danger-muted',
      fallbackBg: 'bg-background-danger-muted',
    },
    dangerlight: {
      fill: 'fill-background-danger',
      fallbackBg: 'bg-background-danger',
    },
    plain: {
      fill: 'transparent',
      fallbackBg: 'bg-transparent',
    },
    black: {
      fill: 'rgb(0 0 0)',
      fallbackBg: 'bg-black',
    },
  };

  const TEXT_COLORS: Record<ButtonVariant, string> = {
    circles: 'text-white',
    primary: 'text-white dark:text-background',
    secondary: 'text-foreground',
    success: 'text-white',
    tertiary: 'text-foreground',
    danger: 'text-white',
    dangerlight: 'text-foreground-danger-muted',
    plain: 'text-inherit',
    black: 'text-white',
  };

  const GAP: Record<ButtonSize, string> = {
    xs: 'gap-1.5',
    sm: 'gap-2',
    md: 'gap-2',
    lg: 'gap-3',
    xl: 'gap-3',
  };

  const PADDING: Record<ButtonSize, string> = {
    xl: 'px-4 py-5',
    lg: 'px-4 py-3',
    md: 'px-3 py-3',
    sm: 'px-3 py-3',
    xs: 'px-3 py-1.5',
  };

  const HEIGHT: Record<ButtonSize, string> = {
    xl: 'h-15',
    lg: 'h-13',
    md: 'h-11',
    sm: 'h-9',
    xs: 'h-8',
  };

  const TEXT: Record<ButtonSize, string> = {
    xl: 'text-[1.125rem] leading-[1.5rem]',
    lg: 'text-[1rem] leading-[1.5rem]',
    md: 'text-[1rem] leading-[1.5rem]',
    sm: 'text-[0.875rem] leading-[1.25rem]',
    xs: 'text-[0.875rem] leading-[1.25rem]',
  };

  const CORNER_RADIUS: Record<ButtonSize, number> = {
    xl: 12,
    lg: 12,
    md: 12,
    sm: 8,
    xs: 8,
  };

  // CSS border-radius fallbacks that approximate squircles
  const FALLBACK_RADIUS: Record<ButtonSize, string> = {
    xl: 'rounded-xl', // 12px
    lg: 'rounded-xl', // 12px
    md: 'rounded-xl', // 12px
    sm: 'rounded-lg', // 8px
    xs: 'rounded-lg', // 8px
  };

  const LAYOUT: Record<Layout, string> = {
    icon: 'aspect-square',
    text: '',
  };

  const JUSTIFY_CONTENT: Record<'center' | 'between' | 'start' | 'end', string> = {
    center: 'justify-center',
    between: 'justify-between',
    start: 'justify-start',
    end: 'justify-end',
  };

  let _disabled = $derived(disabled || pending);
  let _gap = $derived(vertical ? 'gap-1' : GAP[size]);

  // State management for view transitions
  let buttonElement: HTMLButtonElement | null = $state(null);
  let svgPath = $state('');
  let useSquircles = $state(false); // Start with fallback
  let lastDimensions = $state({ width: 0, height: 0, variant: '', size: '', round: false });
  let resizeTimeout: number | undefined;

  const paint = () => {
    if (!buttonElement || !useSquircles || variant === 'plain') return;

    const rect = buttonElement.getBoundingClientRect();
    const strokeWidth = COLORS[variant].strokeWidth || 0;
    const width = rect.width;
    const height = rect.height;

    // Cache check
    const currentDimensions = { width, height, variant, size, round };
    if (
      lastDimensions.width === width &&
      lastDimensions.height === height &&
      lastDimensions.variant === variant &&
      lastDimensions.size === size &&
      lastDimensions.round === round
    ) {
      return;
    }

    lastDimensions = currentDimensions;

    const pathWidth = width - strokeWidth;
    const pathHeight = height - strokeWidth;
    const cornerRadius = round ? Math.min(pathWidth, pathHeight) / 2 : CORNER_RADIUS[size];
    const cornerSmoothing = round ? 1 : 0.65;

    svgPath = getSvgPath({
      width: pathWidth,
      height: pathHeight,
      cornerRadius,
      cornerSmoothing,
    });
  };

  const debouncedPaint = () => {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(paint, 16) as unknown as number;
  };

  // Enable squircles after view transitions complete
  afterViewTransition(() => {
    // Small delay to ensure DOM is fully settled
    setTimeout(() => {
      useSquircles = true;
      paint();
    }, 50);
  });

  onMount(() => {
    addEventListener('resize', debouncedPaint, { passive: true });
  });

  onDestroy(() => {
    removeEventListener('resize', debouncedPaint);
    if (resizeTimeout) clearTimeout(resizeTimeout);
  });

  beforeNavigate(() => {
    useSquircles = false;
    svgPath = '';
  });

  const onClick = (e: Event) => {
    onclick?.(e);
  };

  const viewBoxWidth = $derived(Math.ceil(lastDimensions.width || 200));
  const viewBoxHeight = $derived(Math.ceil(lastDimensions.height || 60));

  // Dynamic classes based on squircle state
  const fallbackClasses = $derived.by(() => {
    if (useSquircles || variant === 'plain') return '';

    const bg = COLORS[variant].fallbackBg || '';
    const border = COLORS[variant].fallbackBorder || '';
    const radius = round ? 'rounded-full' : FALLBACK_RADIUS[size];

    return `${bg} ${border} ${radius}`;
  });

  const shouldShowSvg = $derived(useSquircles && variant !== 'plain' && svgPath);
</script>

<span class="block" class:disabled={_disabled} class:w-full={stretched} class:w-min={icon}>
  <button
    bind:this={buttonElement}
    aria-label={ariaLabel}
    data-icon-full-opacity={fullOpacityIcon}
    data-variant={variant}
    data-size={size}
    {type}
    onclick={onClick}
    disabled={_disabled}
    class:opacity-50={disabled}
    data-testid={testid}
    class="
      group
      typography-body-l-medium
      relative
      z-20
      flex
      w-full
      shrink-0
      select-none
      flex-nowrap
      items-center
      justify-center
      overflow-hidden
      whitespace-nowrap
      {variant === 'plain' || vertical ? '' : HEIGHT[size]}
      {padding ? padding : PADDING[size]} {TEXT[size]} {TEXT_COLORS[variant]}
      {LAYOUT[icon ? 'icon' : 'text']}
      {vertical ? 'h-19' : ''}
      {fallbackClasses}
    ">
    <!-- Squircle SVG (only shown after view transitions) -->
    {#if shouldShowSvg}
      <svg
        class="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 {viewBoxWidth} {viewBoxHeight}"
        preserveAspectRatio="none">
        <path
          d={svgPath}
          stroke={COLORS[variant].stroke}
          stroke-width={COLORS[variant].strokeWidth || 0}
          transform="translate({(COLORS[variant].strokeWidth || 0) / 2}, {(COLORS[variant]
            .strokeWidth || 0) / 2})"
          class="{COLORS[variant].fill} transition-all duration-75 group-active:brightness-90" />
      </svg>
    {/if}

    <span
      class="relative z-10 flex items-center {JUSTIFY_CONTENT[justifyContent]} {_gap}"
      class:w-full={stretched}
      class:opacity-0={pending}
      class:flex-col={vertical}>
      {#if leading}
        {@render leading()}
      {:else if variant !== 'plain'}
        <span></span>
      {/if}

      {@render children?.()}

      {#if trailing}
        {@render trailing()}
      {:else if variant !== 'plain'}
        <span></span>
      {/if}
    </span>

    {#if pending}
      <BrandedSpinner />
    {/if}
  </button>
</span>

<style lang="postcss">
  @reference "tailwindcss";

  button:not(.aspect-square):not([data-icon-full-opacity='true'])
    :global(svg:not(.pointer-events-none)) {
    @apply opacity-70;
  }

  :global(a[disabled]) {
    @apply pointer-events-none;
  }
</style>
